import React, {useEffect, useState} from 'react'

// import {useToast} from '../../../lib/toast'
import {IntegrationType} from '../../../data/workbench-api/generated/types'

import {get} from 'object-path-immutable'
import {IntegrationView} from '.'
import {ButtonGroup, FormSpacer} from '../../../components/form'
import {Button} from '../../../components/library/buttons'
import {CopyInput} from '../../../components/library/copy'
import {TextInput} from '../../../components/library/form'
import {Icon} from '../../../components/library/icon/icon'
import {Modal} from '../../../components/library/modal/modal'
import styled from 'styled-components'
import {Loader} from '../../../components/loader'
import {FormModal} from '../../../components/modals'
import {EnvironmentNode} from '../../../environment'
import {useStateChanged} from '../../../hooks/use-state-changed'
import {useAnalytics} from '../../../lib/analytics/analytics'
import {useToast} from '../../../lib/toast'
import {
  useDeleteIntegrationMutation,
  useGetIntegrationsQuery,
  useUpdateIntegrationMutation
} from './generated'
import {AddModalProps, ConfirmModalProps, EditModalProps} from './modals'
import {ConfirmIntegrationActionModal} from './modals/confirm'
import {gql} from '@apollo/client'
import {
  MarketoIntegrationDetailFragment,
  useAddMarketoClientIntegrationMutation
} from './generated/marketo'
import {ENVIRONMENT_NAME} from '../../../lib/env'

const Info = styled.p`
  display: flex;
  gap: 10px;
  align-items: center;
  margin: 0 10px;
`

type MarketoFormFields = {
  name: string
}

const getServiceProviderUrl = (environment: EnvironmentNode) => {
  if (ENVIRONMENT_NAME === 'local') {
    return `http://localhost:6005/v1/marketo/${environment.id}/connector/spec.json`
  }

  if (ENVIRONMENT_NAME === 'master') {
    return `https://${environment.organisationId}.customer-api.atomic.io/v1/marketo/${environment.id}/connector/spec.json`
  }

  if (ENVIRONMENT_NAME === 'staging') {
    return `https://${environment.organisationId}.customer-api.staging.atomic.io/v1/marketo/${environment.id}/connector/spec.json`
  }

  return `https://${environment.organisationId}.customer-api.${ENVIRONMENT_NAME}.unobtainium.atomic.io/v1/marketo/${environment.id}/connector/spec.json`
}
export const integrationIsValid = (integration: MarketoFormFields) =>
  !!integration

export const Marketo = ({environment}: {environment: EnvironmentNode}) => {
  const analytics = useAnalytics()
  const toast = useToast()

  const {data, loading, refetch} = useGetIntegrationsQuery({
    variables: {
      environmentId: environment.id,
      integrationType: IntegrationType.MarketoSsfs
    }
  })

  const integrations = data?.integrationConfigurations?.nodes ?? []

  useEffect(() => {
    analytics.track({
      event: 'Viewed marketo configuration',
      properties: {
        org_id: environment.organisationId,
        environment_id: environment.id,
        category: 'configuration>adobe>marketo-self-service-flow-steps'
      }
    })
  }, [analytics, environment])

  const description = `
    To enable integrations with Marketo, enter a name for the integration the copy the
    OAuth Secret. It will only be shown once, so make sure you copy it during the creation process.
    `

  return (
    <IntegrationView
      integrations={integrations}
      loading={loading}
      sectionTitle="Marketo Integration"
      sectionDescription={description}
      environment={environment}
      serviceUrl={getServiceProviderUrl(environment)}
      renderAddModal={(canEdit, handleClose) => (
        <AddModal
          environmentId={environment.id}
          organisationId={environment.organisationId}
          onCompleted={() => {
            analytics.track({
              event: 'Added configuration',
              properties: {
                org_id: environment.organisationId,
                environment_id: environment.id,
                category: 'configuration>adobe>marketo-self-service-flow-steps'
              }
            })

            toast('Integration added')

            refetch()
          }}
          handleClose={handleClose}
          canEdit={canEdit}
        />
      )}
      renderEditModal={(integration, canEdit, handleClose) => (
        <EditModal
          integration={integration}
          canEdit={canEdit}
          onCompleted={() => {
            analytics.track({
              event: 'Updated configuration',
              properties: {
                org_id: environment.organisationId,
                environment_id: environment.id,
                category: 'configuration>adobe>marketo-self-service-flow-steps'
              }
            })
            toast('Integration updated')

            refetch()

            handleClose()
          }}
          handleClose={handleClose}
        />
      )}
      renderConfirmModal={(action, integration, handleClose) => {
        return (
          <ConfirmModal
            mode={action}
            integration={integration}
            handleClose={handleClose}
            onCompleted={refetch}
          />
        )
      }}
    />
  )
}

// TODO: think about more abstractions here to reduce duplication

const AddModal = ({
  environmentId,
  handleClose,
  onCompleted,
  canEdit
}: AddModalProps) => {
  const [localObj, setLocalObj, dirty] = useStateChanged<MarketoFormFields>({
    name: ''
  })

  const [newIntegration, setNewIntegration] = useState<
    MarketoIntegrationDetailFragment | undefined
  >(undefined)

  const [doMutation, {loading, error}] = useAddMarketoClientIntegrationMutation(
    {
      onCompleted: data => {
        setNewIntegration(
          data?.createMarketoIntegrationConfiguration
            ?.integrationConfiguration ?? undefined
        )

        onCompleted()
      }
    }
  )

  return !newIntegration ? (
    <FormModal
      title="Add integration"
      handleClose={handleClose}
      onSubmit={() => {
        doMutation({
          variables: {
            environmentId,
            integrationType: IntegrationType.MarketoSsfs,
            ...localObj
          }
        })
      }}
      error={error}
      isValid={dirty && !loading && integrationIsValid(localObj)}
    >
      <>
        {loading && <Loader />}
        {!loading && (
          <TextInput
            upLabel="Name"
            label="Enter a name for this integration..."
            value={localObj.name || ''}
            onChange={name => setLocalObj(c => ({...c, name}))}
            readOnly={!canEdit}
          />
        )}
      </>
    </FormModal>
  ) : (
    <Modal title="Add integration" handleClose={handleClose}>
      <>
        <CopyInput
          value={get(newIntegration.configuration, 'clientId') ?? '-'}
          copyText="Copy OAuth Client ID"
          size={'small'}
        />
        <Info>
          <Icon name="info" />
          <p>
            This secret will not be displayed again. Please save it before
            closing this modal.
          </p>
        </Info>
        <CopyInput
          value={get(newIntegration?.configuration, 'clientSecret') ?? '-'}
          copyText="Copy OAuth Client Secret"
          size={'small'}
        />
        <ButtonGroup>
          <Button
            size="large"
            onClick={() => {
              setNewIntegration(undefined)
              handleClose()
            }}
          >
            Close
          </Button>
        </ButtonGroup>
      </>
    </Modal>
  )
}

const EditModal = ({
  integration,
  handleClose,
  onCompleted,
  canEdit
}: EditModalProps) => {
  const [localObj, setLocalObj, dirty] = useStateChanged<MarketoFormFields>({
    name: integration.name
  })
  const [doMutation, {loading, error}] = useUpdateIntegrationMutation({
    onCompleted
  })

  return (
    <FormModal
      readOnly={!canEdit}
      title={canEdit ? 'Edit integration' : 'View integration'}
      handleClose={handleClose}
      onSubmit={() => {
        return doMutation({
          variables: {
            environmentId: integration.environmentId,
            id: integration.id,
            ...localObj
          }
        })
      }}
      error={error}
      isValid={dirty && !loading && integrationIsValid(localObj)}
    >
      <>
        <TextInput
          upLabel="Name"
          label="Enter a name for this integration..."
          value={localObj.name || ''}
          onChange={name => setLocalObj(c => ({...c, name}))}
          readOnly={!canEdit}
        />
        <FormSpacer />
        <CopyInput
          value={get(integration.configuration, 'clientId') ?? '-'}
          copyText="Copy OAuth Client ID"
          size={'small'}
        />
      </>
    </FormModal>
  )
}

const ConfirmModal = ({
  integration,
  handleClose,
  onCompleted,
  mode
}: ConfirmModalProps) => {
  const toast = useToast()

  const [update] = useUpdateIntegrationMutation()
  const [deleteMutation] = useDeleteIntegrationMutation()

  return (
    <ConfirmIntegrationActionModal
      integration={integration}
      mode={mode}
      /* note if you need a custom message, you can pass a function here  description={"foobar"}*/
      onClose={handleClose}
      onConfirm={() => {
        if (mode === 'disable') {
          update({
            variables: {
              environmentId: integration.environmentId,
              id: integration.id,
              enabled: false
            }
          })
            .then(() => {
              toast('Integration disabled', 'success')
            })
            .catch(e => {
              toast('Failed to disable integration', 'error')
              throw e
            })
        } else if (mode === 'delete') {
          deleteMutation({
            variables: {
              environmentId: integration.environmentId,
              id: integration.id
            }
          })
            .catch(e => {
              toast('Failed to delete integration', 'error')
              throw e
            })
            .then(() => {
              toast('Integration deleted', 'success')
            })
        }

        onCompleted()
      }}
    />
  )
}

gql`
  mutation AddMarketoClientIntegration(
    $environmentId: String!
    $name: String!
    $integrationType: IntegrationType!
  ) {
    createMarketoIntegrationConfiguration(
      input: {
        environmentId: $environmentId
        name: $name
        enabled: true
        integrationType: $integrationType
      }
    ) {
      integrationConfiguration {
        ...MarketoIntegrationDetail
      }
    }
  }

  fragment MarketoIntegrationDetail on MarketoIntegrationConfiguration {
    id
    integrationType
    environmentId
    name
    enabled
    created
    configuration
  }
`
